import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { recoverSession, scopeForPath } from "@/lib/session-vault";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireAdminOrApprover?: boolean;
}

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireAdminOrApprover = false,
}: ProtectedRouteProps) => {
  const { user, loading, isAdmin, isSuperAdmin, isApprover, mfaRequired, roleError, refreshRoles } = useAuth();
  const location = useLocation();

  /**
   * Transient-null resilience. supabase-js clears the session from storage when
   * a single refresh call fails (iframe suspension, network blip) even though the
   * server session is still valid — the old guard read `user === null` and bounced
   * straight to /login. Give it exactly ONE recovery attempt (client storage →
   * explicit refresh → scope vault) before redirecting.
   */
  const [settled, setSettled] = useState(false);
  const attemptedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (user) {
      attemptedRef.current = false;
      setSettled(false);
      return;
    }
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    let cancelled = false;
    const timer = setTimeout(async () => {
      let session = null;
      try {
        session = await recoverSession(scopeForPath(location.pathname));
      } catch {
        session = null;
      }
      if (cancelled) return;
      // A recovered session arrives via onAuthStateChange; if nothing came back
      // the visitor really is not authenticated → let the redirect through.
      setSettled(true);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [loading, user, location.pathname]);


  if (loading) return <Spinner />;

  const requiresAuth = requireAdmin || requireSuperAdmin || requireAdminOrApprover;

  // Role READ FAILURE is not the same as "no privileges" and not the same as
  // "not authenticated" — surface it as retryable instead of Access Denied.
  if (user && requiresAuth && roleError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-sm space-y-3 text-center">
          <p className="text-lg font-semibold">Couldn't verify your permissions</p>
          <p className="text-sm text-muted-foreground">{roleError}</p>
          <button
            onClick={() => { void refreshRoles(); }}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Every guarded route — consumer, creator or staff — sends a logged-out
  // visitor to the ONE login screen, carrying where they were headed so they
  // land back there instead of on an empty state.
  if (!user) {
    // Still inside the single recovery attempt (or it succeeded and the context
    // has not re-rendered yet) → hold the spinner rather than bounce.
    if (!settled) return <Spinner />;
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  // MFA gate: if the user has enrolled MFA but hasn't completed the AAL2
  // challenge yet, bounce them to the unified login where MfaChallenge mounts.
  // Defense-in-depth against deep links and page refreshes at AAL1.
  if (mfaRequired && requiresAuth) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }


  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold">Access Denied</p>
          <p className="text-sm text-muted-foreground">This area is restricted to platform administrators.</p>
        </div>
      </div>
    );
  }

  if (requireAdminOrApprover && !(isAdmin || isApprover)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold">Access Denied</p>
          <p className="text-sm text-muted-foreground">You don't have admin or approver privileges.</p>
        </div>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold">Access Denied</p>
          <p className="text-sm text-muted-foreground">You don't have admin privileges.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
